import { buffers } from 'redux-saga'
import { call, put, take, fork, select, actionChannel, all, takeLatest, takeEvery } from 'redux-saga/effects'
import actionTypes from '../actions/actionTypes'
import { userSync, featureFlagsSync } from '../../sync'
import { privateLibrarySelectors } from 'redux/selectors'
import currentViewSelector from 'redux/selectors/ui/currentViewSelector'
import callAny from './helpers/call_any'
import userActions from 'redux/actions/userActions'
import cookiesActions from 'redux/actions/cookiesActions'
import formatError from 'redux/sagas/helpers/error_formatter'
import viewTypes from 'helpers/enums/viewTypes'
import tooltipsTypes from 'helpers/enums/tooltipTypes'
import topInformationTypes from 'helpers/enums/topInformationTypes'
import userCannySelector from 'redux/selectors/user/user_canny_selector'
import canny from 'helpers/canny'
import variants from 'helpers/enums/noResultsVariants'


function *getUserExtraData() {
	const initializeViewChan = yield actionChannel([
		actionTypes.THEMES_INITIALIZE,
		actionTypes.PRIVATE_LIBRARY_SETTINGS_FETCHED,
		actionTypes.PRIVATE_LIBRARY_INITIALIZE_PREVIEW,
		actionTypes.STOREFRONT_OWNER_INITIALIZE,
		actionTypes.STOREFRONT_VISITOR_INITIALIZE,
		actionTypes.STOREFRONT_INITIALIZE_PREVIEW,
		actionTypes.MY_ACCOUNT_INITIALIZE_BASE,
		actionTypes.BINDERS_INITIALIZE,
		actionTypes.QUIZ_INITIALIZE,
		actionTypes.DRAW_IT_INITIALIZE_CREATION_TOOL,
		actionTypes.MATCHING_PAIRS_CT_INITIALIZE_ROUTE,
		actionTypes.MY_LIBRARY_INITIALIZE,
		actionTypes.MY_LIBRARY_PREVIEW_INITIALIZE,
		actionTypes.STORE_CONNECTOR_STORE_TOPBAR_INITIALIZE,
		actionTypes.DM_STORAGE_EXPERIMENT_REFER_CONTACT_INITIALIZE,
		actionTypes.POLL_INITIALIZE_CREATION_TOOL,
		actionTypes.DESMOS_INITIALIZE_CREATION_TOOL,
		actionTypes.DESMOS_INITIALIZE_CREATION_TOOL_STANDALONE,
		actionTypes.SIDEBAR_INITIALIZE,
		actionTypes.MAIN_TOPBAR_INITIALIZE,
		actionTypes.FLOCABULARY_INITIALIZE_SUCCESS,
		actionTypes.GOOGLE_SLIDES_INITIALIZE_SUCCESS,
		actionTypes.VIDEO_STANDALONE_INITIALIZE_PICKER_ROUTE,
		actionTypes.VIDEO_STANDALONE_INITIALIZE_EDIT_ROUTE,
		actionTypes.GAME_INITIALIZE_STANDALONE,
		actionTypes.DRAW_IT_INITIALIZE_STANDALONE,
		actionTypes.MATCHING_PAIRS_INITIALIZE_STANDALONE,
	], buffers.sliding(6))

	let currentView = ''
	let isLoaded = false

	while (true) {
		yield take(initializeViewChan)
		const view = yield select(currentViewSelector)

		if ((view !== currentView && view !== '') || !isLoaded) {

			isLoaded = true
			currentView = view
			let environment = ''
			switch (view) {
				case viewTypes.SCHOOL_LIBRARY:
				case viewTypes.DISTRICT_LIBRARY:
				case viewTypes.PRIVATE_LIBRARY:
				case 'school-library':
				case 'district-library':
				{
					const currentLibraryType = yield select(privateLibrarySelectors.getCurrentLibraryType)
					if (currentLibraryType === 'school') {
						environment = 'school-library'
					} else if (currentLibraryType === 'district') {
						environment = 'district-library'
					} else {
						environment = 'app'
					}
					break
				}
				case viewTypes.SUB_PLANS:
					environment = 'binders'
					break
				case viewTypes.FLOCABULARY_CREATION:
					environment = view
					break
				default:
					environment = 'app'
			}

			try {
				const response = yield callAny(userSync.getUserExtraData, environment)
				yield put({
					type: actionTypes.USER_EXTRA_DATA,
					payload: response.payload,
					environment,
				})
			} catch (error) {
				const formattedError = formatError(error)
				yield put({
					type: actionTypes.SYNC_UNCAUGHT_ERROR,
					payload: formattedError.payload ? formattedError.payload : formattedError,
				})
			}
		}
	}
}

function *updateUserTooltip(action) {
	try {
		yield callAny(userSync.updateTooltip, action.tooltipName)
	} catch (error) {
		const serverError = formatError(error)
		yield put(userActions.saveTooltipError(serverError))
	}
}

function *updateMultipleUserTooltips(action) {
	try {
		yield all(action.tooltipNames.map(name => callAny(userSync.updateTooltip, name)))
	} catch (error) {
		const serverError = formatError(error)
		yield put(userActions.saveTooltipError(serverError))
	}
}

function *watchGetUserData() {
	yield take([actionTypes.PRIVATE_LIBRARY_INITIALIZE_PREVIEW,
		actionTypes.STOREFRONT_INITIALIZE_PREVIEW,
		actionTypes.USER_GET_DATA,
		actionTypes.ONBOARDING_INITIALIZE,
		actionTypes.ADMIN_ONBOARDING_INITIALIZE,
		actionTypes.ADMIN_ONBOARDING_INITIALIZE_INVITE_VIEW,
		actionTypes.ADMIN_ONBOARDING_INITIALIZE_SCHOOL_VIEW,
		actionTypes.MY_ACCOUNT_INITIALIZE_BASE,
		actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_INITIALIZE,
		actionTypes.MAIN_TOPBAR_INITIALIZE,
		actionTypes.GOOGLE_SLIDES_INITIALIZE,
		actionTypes.PRIVATE_LIBRARY_INITIALIZE_LIBRARY_BASE,
		actionTypes.DEVICE_NOT_SUPPORTED_INITIALIZE,
		actionTypes.MAIN_SIDEBAR_CONNECTOR_INITIALIZE,
		actionTypes.VIDEO_STANDALONE_INITIALIZE_EDIT_ROUTE,
		actionTypes.VIDEO_STANDALONE_INITIALIZE_PICKER_ROUTE,
		actionTypes.NEW_TERMS_AND_CONDITIONS_INITIALIZE])
	while (true) {
		try {
			const response = yield callAny(userSync.getUserData)
			yield put(userActions.userDataFetched(response.payload, true))
			const complementary_data_response = yield callAny(userSync.getComplementaryData, response.payload.userId)
			yield put(userActions.userComplementaryDataFetched(complementary_data_response))
		} catch (error) {
			yield put({
				type: actionTypes.SYNC_UNCAUGHT_ERROR,
				error: error.serverError,
			})
		}
		yield take([
			actionTypes.MY_LIBRARY_SCHOOL_INVITE_CONFIRM_YES_SUCCESS,
			actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRMED_SUCCESS,
		])
	}
}

function *sendHelp(action) {
	try {
		const response = yield call(userSync.sendHelp, action.text, action.title)
		if (response.error_code === 0) {
			yield put({
				type: actionTypes.HELPBOX_SEND_HELP_SUCCESS,
				message: response.message,
			})
		} else {
			yield put({
				type: actionTypes.HELPBOX_SEND_HELP_ERROR,
				message: response.message,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *trackUserAction(action) {
	try {
		if (action.button_data && action.button_data.trackActionParams) {
			yield callAny(userSync.trackUserAction, action.button_data.trackActionParams)
		}
	} catch (err) {}
}

function *getFeatureFlags() {
	try {
		const response = yield callAny(featureFlagsSync.getFeatureFlags)
		yield put(userActions.getFeatureFlagsSuccess(response.payload.feature_flags))
	} catch (error) {}
}

function *updateUserTooltipByUid(action) {
	try {
		switch (action.uid) {
			case topInformationTypes.GOOGLE_SLIDES_CANNOT_COPY_PASTE_SLIDES:
				yield call(
					userSync.updateTooltip,
					tooltipsTypes.GOOGLE_SLIDES_CANNOT_COPY_PASTE_SLIDES
				)
				break
		}
	} catch (error) {}
}

function *updateUserTooltipByType(action) {
	try {
		const tooltips = ['tootooltipOBLiveSession', tooltipsTypes.NICKEL_TOUR]
		if (tooltips.includes(action.tooltipType)) {
			yield call(userSync.updateTooltip, action.tooltipType)
		}
	} catch (error) {}
}

function *testCookies() {
	yield take(actionTypes.APP_INITIALIZE)
	while (true) {
		try {
			yield callAny(userSync.setTestCookie)
			yield callAny(userSync.getTestCookie)
		} catch (err) {
			yield put(cookiesActions.cookieTestFailed(formatError(err)))
		}
		yield take(actionTypes.USER_COOKIE_BLOCKED_RETRY)
	}
}

function *updateTooltipCollaborateHomeworkDoNotShowAgain(action) {
	if (action.doNotShowAgain) {
		yield call(userSync.updateTooltip, tooltipsTypes.STUDENT_PACED_LAUNCHED_WITH_COLLABORATE)
	}
}

function *identifyUser(action) {
	if (action.variant !== variants.FLOCABULARY) {
		try {
			const cannyProperties = yield select(userCannySelector)
			yield call(canny.identify, cannyProperties)
			yield put(userActions.identifyCannyUserSuccess(action.boardType))
		} catch (error) {
			const serverError = formatError(error)
			yield put(userActions.identifyCannyUserError(serverError))
		}
	}
}

function *acceptTermsAndConditions() {
	try {
		yield callAny(userSync.acceptTermsAndConditions)
		yield put(userActions.acceptTermsAndConditionsSuccess())
	} catch (error) {
		yield put(userActions.acceptTermsAndConditionsError())
	}
}

function *getPermissions() {
	try {
		const response = yield callAny(userSync.getPermissions)
		yield put(userActions.getPermissionsSuccess(response.payload))
	} catch (error) {
		yield put(userActions.getPermissionsFailed())
	}
}

export default [
	fork(getUserExtraData),
	takeEvery([
		actionTypes.USER_UPDATE_TOOLTIP,
		actionTypes.INTERACTIVE_VIDEO_CREATION_EDITOR_WELCOME_TOUR_LETS_DO_IT,
		actionTypes.INTERACTIVE_VIDEO_CREATION_WELCOME_MODAL_LETS_DO_IT,
		actionTypes.INTERACTIVE_VIDEO_CREATION_EDITOR_WELCOME_TOUR_STEP_1_GOT_IT,
		actionTypes.INTERACTIVE_VIDEO_CREATION_EDITOR_WELCOME_TOUR_STEP_2_GOT_IT,
		actionTypes.INTERACTIVE_VIDEO_CREATION_EDITOR_WELCOME_TOUR_STEP_3_GOT_IT,
		actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_TRIM_TOOLTIP_GOT_IT,
		actionTypes.INTERACTIVE_VIDEO_TOOLTIP_CHECK_PROGRESS_DISMISSED,
	], updateUserTooltip),
	takeLatest(
		actionTypes.INTERACTIVE_VIDEO_CREATION_EDITOR_WELCOME_TOUR_SKIP_TOUR,
		updateMultipleUserTooltips
	),
	takeEvery(actionTypes.CLOSE_TOP_INFORMATION, updateUserTooltipByUid),
	fork(watchGetUserData),
	takeLatest(actionTypes.HELPBOX_SEND_HELP, sendHelp),
	takeLatest(actionTypes.USER_UPGRADE_ACTION_SELECTED, trackUserAction),
	takeEvery([
		actionTypes.APP_INITIALIZE,
		actionTypes.USER_IS_LOGGED,
		actionTypes.MATCHING_PAIRS_TEACHER_INITIALIZE,
		actionTypes.STORE_CONNECTOR_SEARCH_INITIALIZE,
		actionTypes.STORE_CONNECTOR_STORE_TOPBAR_INITIALIZE,
		actionTypes.MAIN_TOPBAR_CONNECTOR_INITIALIZE,
		actionTypes.GOOGLE_SLIDES_INITIALIZE,
	], getFeatureFlags),
	takeLatest(
		actionTypes.USER_TOOLTIP_INITIALIZE, updateUserTooltipByType
	),
	takeLatest([
		actionTypes.MY_LIBRARY_LAUNCH_HOMEWORK_COLLABORATE_DISABLED_OPEN_SETTINGS,
		actionTypes.MY_LIBRARY_LAUNCH_HOMEWORK_COLLABORATE_DISABLED_LAUNCH,
	], updateTooltipCollaborateHomeworkDoNotShowAgain),
	takeLatest([
		actionTypes.STORE_SEARCH_NO_RESULTS_FEEDBACK_SELECTED,
		actionTypes.USER_SEND_SUGGESTIONS_BUTTON_SELECTED,
	], identifyUser),
	takeLatest(
		actionTypes.USER_ACCEPT_TERMS_AND_CONDITIONS, acceptTermsAndConditions
	),
	takeLatest(actionTypes.VIDEO_STORE_PICKER_INITIALIZED, getPermissions),
	fork(testCookies),
]

export {
	updateUserTooltipByUid,
	updateUserTooltipByType,
	testCookies,
	updateUserTooltip,
	updateMultipleUserTooltips,
}
